// utils/fastenerTransform.js
// Утилиты для трансформации крепежей

/**
 * Трансформирует shapes крепежа при изменении межосевого расстояния
 * CLAMP раздвигается от середины между центрами зажимов
 * FASTENER раздвигается от своего центра
 * 
 * @param {Array} shapes - Массив shapes из DXF (уже нормализованные к (0,0))
 * @param {number} targetAxialDistance - Целевое межосевое расстояние в мм
 * @param {number} baseAxialDistance - Базовое межосевое расстояние (по умолчанию 300)
 * @param {number} baseFastenerHeight - Базовая высота FASTENER слоя (по умолчанию 480)
 * @returns {Array} - Трансформированный массив shapes
 */
export function transformFastenerShapes(
  shapes,
  targetAxialDistance,
  baseAxialDistance = 300,
  baseFastenerHeight = 480
) {
  if (!shapes || shapes.length === 0) return shapes;

  // Находим центры зажимов CLAMP и центр FASTENER
  const clampShapes = shapes.filter(s => s.layer === 'CLAMP');
  const fastenerShapes = shapes.filter(s => s.layer === 'FASTENER');
  
  let upperClampCenterY = null;
  let lowerClampCenterY = null;
  let clampCenterY = null; // Центр между зажимами CLAMP
  let fastenerCenterY = null; // Центр FASTENER слоя
  
  // Вычисляем центр CLAMP
  if (clampShapes.length > 0) {
    // Собираем все Y координаты из CLAMP слоя
    const yCoords = [];
    clampShapes.forEach(shape => {
      if (shape.type === 'line' || shape.type === 'polyline') {
        const points = shape.points;
        for (let i = 1; i < points.length; i += 2) {
          yCoords.push(points[i]);
        }
      } else if (shape.type === 'circle' || shape.type === 'arc') {
        yCoords.push(shape.y - shape.radius);
        yCoords.push(shape.y + shape.radius);
      }
    });

    if (yCoords.length > 0) {
      // Сортируем Y координаты
      yCoords.sort((a, b) => a - b);

      // Находим разрыв между верхним и нижним зажимами
      // Стратегия: ищем самый большой разрыв в средней трети диапазона
      const totalHeight = yCoords[yCoords.length - 1] - yCoords[0];
      const minY = yCoords[0];
      const middleStart = minY + totalHeight * 0.33;
      const middleEnd = minY + totalHeight * 0.67;
      
      const estimatedClampHeight = totalHeight / 2;
      const minGapThreshold = Math.max(1, estimatedClampHeight * 0.03);
      
      let maxGap = 0;
      let gapIndex = 0;
      
      // Сначала ищем в средней трети
      for (let i = 0; i < yCoords.length - 1; i++) {
        const y = yCoords[i];
        const gap = yCoords[i + 1] - yCoords[i];
        if (y >= middleStart && y <= middleEnd && gap > maxGap && gap > minGapThreshold) {
          maxGap = gap;
          gapIndex = i;
        }
      }
      
      // Если не нашли в средней трети, ищем по всему диапазону
      if (maxGap === 0) {
        for (let i = 0; i < yCoords.length - 1; i++) {
          const gap = yCoords[i + 1] - yCoords[i];
          if (gap > maxGap && gap > minGapThreshold) {
            maxGap = gap;
            gapIndex = i;
          }
        }
      }

      // Если все еще не нашли, используем середину массива
      if (maxGap === 0) {
        gapIndex = Math.floor(yCoords.length / 2) - 1;
      }

      // Разделяем на верхний и нижний зажимы
      const upperClampYs = yCoords.slice(0, gapIndex + 1);
      const lowerClampYs = yCoords.slice(gapIndex + 1);

      // Проверяем, что оба массива не пустые
      if (upperClampYs.length > 0 && lowerClampYs.length > 0) {
        // Вычисляем центры зажимов
        upperClampCenterY = (Math.min(...upperClampYs) + Math.max(...upperClampYs)) / 2;
        lowerClampCenterY = (Math.min(...lowerClampYs) + Math.max(...lowerClampYs)) / 2;

        // Центр между зажимами CLAMP
        clampCenterY = (upperClampCenterY + lowerClampCenterY) / 2;
      } else {
        // Fallback: если не удалось разделить, используем весь диапазон
        clampCenterY = (yCoords[0] + yCoords[yCoords.length - 1]) / 2;
      }
    }
  }
  
  // ВАЖНО: Рассчитываем разницу на основе ТЕКУЩЕГО межосевого расстояния
  // (после scaleClampLayer межосевое могло измениться!)
  let currentAxialDistance = baseAxialDistance;
  if (upperClampCenterY !== null && lowerClampCenterY !== null) {
    currentAxialDistance = lowerClampCenterY - upperClampCenterY;
  }
  
  // Если текущее межосевое уже равно целевому, ничего не делаем
  if (Math.abs(currentAxialDistance - targetAxialDistance) < 0.01) {
    return shapes;
  }
  
  // Вычисляем разницу и сдвиг для каждой половины
  const difference = targetAxialDistance - currentAxialDistance;
  const halfShift = difference / 2;

  // Вычисляем центр FASTENER
  if (fastenerShapes.length > 0) {
    let fastenerMinY = Infinity;
    let fastenerMaxY = -Infinity;

    fastenerShapes.forEach(shape => {
      if (shape.type === 'line' || shape.type === 'polyline') {
        const points = shape.points;
        for (let i = 1; i < points.length; i += 2) {
          const y = points[i];
          fastenerMinY = Math.min(fastenerMinY, y);
          fastenerMaxY = Math.max(fastenerMaxY, y);
        }
      } else if (shape.type === 'circle') {
        fastenerMinY = Math.min(fastenerMinY, shape.y - shape.radius);
        fastenerMaxY = Math.max(fastenerMaxY, shape.y + shape.radius);
      } else if (shape.type === 'arc') {
        fastenerMinY = Math.min(fastenerMinY, shape.y - shape.radius);
        fastenerMaxY = Math.max(fastenerMaxY, shape.y + shape.radius);
      }
    });

    if (fastenerMinY !== Infinity && fastenerMaxY !== -Infinity) {
      fastenerCenterY = (fastenerMinY + fastenerMaxY) / 2;
    }
  }

  // Fallback: если не нашли центры, используем базовые значения
  if (clampCenterY === null) {
    clampCenterY = baseFastenerHeight / 2;
  }
  if (fastenerCenterY === null) {
    fastenerCenterY = baseFastenerHeight / 2;
  }

  // Трансформируем shapes: каждый слой раздвигается от своего центра
  const transformed = shapes.map(shape => {
    if (shape.type === 'line' || shape.type === 'polyline') {
      const newPoints = [...shape.points];
      for (let i = 1; i < newPoints.length; i += 2) {
        const y = newPoints[i];
        
        if (shape.layer === 'CLAMP') {
          // CLAMP раздвигается от середины между зажимами
          if (y < clampCenterY) {
            newPoints[i] = y - halfShift;
          } else if (y > clampCenterY) {
            newPoints[i] = y + halfShift;
          }
        } else if (shape.layer === 'FASTENER') {
          // FASTENER раздвигается от своего центра
          if (y < fastenerCenterY) {
            newPoints[i] = y - halfShift;
          } else if (y > fastenerCenterY) {
            newPoints[i] = y + halfShift;
          }
        } else {
          // Другие слои раздвигаются от центра FASTENER
          if (y < fastenerCenterY) {
            newPoints[i] = y - halfShift;
          } else if (y > fastenerCenterY) {
            newPoints[i] = y + halfShift;
          }
        }
      }
      return { ...shape, points: newPoints };
    } else if (shape.type === 'circle') {
      const y = shape.y;
      let newY = y;
      
      if (shape.layer === 'CLAMP') {
        if (y < clampCenterY) {
          newY = y - halfShift;
        } else if (y > clampCenterY) {
          newY = y + halfShift;
        }
      } else if (shape.layer === 'FASTENER') {
        if (y < fastenerCenterY) {
          newY = y - halfShift;
        } else if (y > fastenerCenterY) {
          newY = y + halfShift;
        }
      } else {
        if (y < fastenerCenterY) {
          newY = y - halfShift;
        } else if (y > fastenerCenterY) {
          newY = y + halfShift;
        }
      }
      
      return { ...shape, y: newY };
    } else if (shape.type === 'arc') {
      const y = shape.y;
      let newY = y;
      
      if (shape.layer === 'CLAMP') {
        if (y < clampCenterY) {
          newY = y - halfShift;
        } else if (y > clampCenterY) {
          newY = y + halfShift;
        }
      } else if (shape.layer === 'FASTENER') {
        if (y < fastenerCenterY) {
          newY = y - halfShift;
        } else if (y > fastenerCenterY) {
          newY = y + halfShift;
        }
      } else {
        if (y < fastenerCenterY) {
          newY = y - halfShift;
        } else if (y > fastenerCenterY) {
          newY = y + halfShift;
        }
      }
      
      return { ...shape, y: newY };
    }

    return shape;
  });

  // Среднее отверстие планки лежит на линии разреза и уезжает вместе с нижней
  // половиной, поэтому шаг D до низа остаётся базовым. По чертежу оба шага равны:
  // отверстие стоит посередине между крайними, а крайние остаются в 20 мм от торцов.
  const holes = [];
  transformed.forEach((shape, index) => {
    if (shape.type !== 'circle' || shape.layer !== 'FASTENER') return;
    if ((shape.radius || 0) > 8) return;
    holes.push({ index, y: shape.y });
  });
  if (holes.length === 3) {
    holes.sort((a, b) => a.y - b.y);
    transformed[holes[1].index] = {
      ...transformed[holes[1].index],
      y: (holes[0].y + holes[2].y) / 2,
    };
  }
  return transformed;
}

/**
 * Вычисляет новые границы (bounding box) для трансформированных shapes крепежа
 * 
 * @param {Array} shapes - Массив трансформированных shapes
 * @param {string} layerFilter - Опциональный фильтр по слою
 * @returns {Object} - { minX, minY, maxX, maxY, width, height }
 */
export function calculateFastenerBounds(shapes, layerFilter = null) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  shapes.forEach(shape => {
    // Если указан фильтр по слою, пропускаем shapes других слоев
    if (layerFilter && shape.layer !== layerFilter) return;
    
    if (shape.type === 'line' || shape.type === 'polyline') {
      const points = shape.points;
      for (let i = 0; i < points.length; i += 2) {
        const x = points[i];
        const y = points[i + 1];
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
      }
    } else if (shape.type === 'circle') {
      minX = Math.min(minX, shape.x - shape.radius);
      maxX = Math.max(maxX, shape.x + shape.radius);
      minY = Math.min(minY, shape.y - shape.radius);
      maxY = Math.max(maxY, shape.y + shape.radius);
    } else if (shape.type === 'arc') {
      minX = Math.min(minX, shape.x - shape.radius);
      maxX = Math.max(maxX, shape.x + shape.radius);
      minY = Math.min(minY, shape.y - shape.radius);
      maxY = Math.max(maxY, shape.y + shape.radius);
    }
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Масштабирует ТОЛЬКО слой CLAMP ТОЛЬКО по Y в зависимости от диаметра хомутов
 * Базовый диаметр: 88.9 мм → высота CLAMP: 92.9 мм (88.9 + 2 + 2)
 * Слой FASTENER НЕ трогаем!
 * 
 * @param {Array} shapes - Массив shapes
 * @param {number} targetClampDiameter - Целевой диаметр хомутов в мм
 * @param {number} baseClampDiameter - Базовый диаметр хомутов (по умолчанию 88.9)
 * @returns {Array} - Трансформированный массив shapes
 */
function scaleClampFromBottom(shape, bottomY, scaleY) {
  if (shape.layer !== 'CLAMP') return shape;
  if (shape.type === 'line' || shape.type === 'polyline') {
    const newPoints = [...shape.points];
    for (let i = 1; i < newPoints.length; i += 2) {
      const y = newPoints[i];
      newPoints[i] = bottomY - (bottomY - y) * scaleY;
    }
    return { ...shape, points: newPoints };
  }
  if (shape.type === 'circle' || shape.type === 'arc') {
    return {
      ...shape,
      y: bottomY - (bottomY - shape.y) * scaleY,
      radius: shape.radius * scaleY,
    };
  }
  return shape;
}

export function scaleClampLayer(shapes, targetClampDiameter, baseClampDiameter = 88.9) {
  if (!shapes || shapes.length === 0) return shapes;
  if (targetClampDiameter === baseClampDiameter) return shapes;

  // Вычисляем высоты CLAMP
  const baseClampHeight = baseClampDiameter + 4; // 88.9 + 2 + 2 = 92.9
  const targetClampHeight = targetClampDiameter + 4; // например, 101.6 + 2 + 2 = 105.6
  const scaleY = targetClampHeight / baseClampHeight;

  // Находим границы каждого CLAMP (верхний и нижний)
  const clampShapes = shapes.filter(s => s.layer === 'CLAMP');
  
  if (clampShapes.length === 0) return shapes;

  // Собираем все Y координаты из CLAMP слоя
  const yCoords = [];
  clampShapes.forEach(shape => {
    if (shape.type === 'line' || shape.type === 'polyline') {
      const points = shape.points;
      for (let i = 1; i < points.length; i += 2) {
        yCoords.push(points[i]);
      }
    } else if (shape.type === 'circle' || shape.type === 'arc') {
      yCoords.push(shape.y - shape.radius);
      yCoords.push(shape.y + shape.radius);
    }
  });

  if (yCoords.length === 0) return shapes;

  // Сортируем Y координаты
  yCoords.sort((a, b) => a - b);

  const span = yCoords[yCoords.length - 1] - yCoords[0];
  const distinct = [];
  yCoords.forEach((y) => {
    if (!distinct.length || y - distinct[distinct.length - 1] > 0.5) distinct.push(y);
  });
  // Один хомут: контур не выше своего диаметра или это прямоугольник из двух кромок.
  // У двух хомутов слой заметно выше одного диаметра и внутри много линий.
  if (distinct.length <= 2 || span <= baseClampHeight * 1.25) {
    const bottomY = yCoords[yCoords.length - 1];
    return shapes.map((shape) => scaleClampFromBottom(shape, bottomY, scaleY));
  }

  // Находим разрыв между верхним и нижним зажимами
  // Стратегия: ищем самый большой разрыв в средней трети диапазона
  const totalHeight = yCoords[yCoords.length - 1] - yCoords[0];
  const minY = yCoords[0];
  const middleStart = minY + totalHeight * 0.33;
  const middleEnd = minY + totalHeight * 0.67;
  
  const estimatedClampHeight = totalHeight / 2;
  const minGapThreshold = Math.max(1, estimatedClampHeight * 0.03);
  
  let maxGap = 0;
  let gapIndex = 0;
  
  // Сначала ищем в средней трети
  for (let i = 0; i < yCoords.length - 1; i++) {
    const y = yCoords[i];
    const gap = yCoords[i + 1] - yCoords[i];
    if (y >= middleStart && y <= middleEnd && gap > maxGap && gap > minGapThreshold) {
      maxGap = gap;
      gapIndex = i;
    }
  }
  
  // Если не нашли в средней трети, ищем по всему диапазону
  if (maxGap === 0) {
    for (let i = 0; i < yCoords.length - 1; i++) {
      const gap = yCoords[i + 1] - yCoords[i];
      if (gap > maxGap && gap > minGapThreshold) {
        maxGap = gap;
        gapIndex = i;
      }
    }
  }

  // Если все еще не нашли, используем середину массива
  if (maxGap === 0) {
    gapIndex = Math.floor(yCoords.length / 2) - 1;
  }

  // Разделяем на верхний и нижний зажимы
  const upperClampYs = yCoords.slice(0, gapIndex + 1);
  const lowerClampYs = yCoords.slice(gapIndex + 1);

  // Проверяем, что оба массива не пустые
  if (upperClampYs.length === 0 || lowerClampYs.length === 0) {
    console.warn('Не удалось разделить зажимы CLAMP на верхний и нижний');
    return shapes;
  }

  // Вычисляем границы зажимов ДО масштабирования
  const upperClampMaxY = Math.max(...upperClampYs);
  const lowerClampMaxY = Math.max(...lowerClampYs);

  // ВАЖНО: Нижняя стенка каждого зажима остаётся на месте!
  // Масштабируем от нижней стенки вверх
  const upperClampBottomY = upperClampMaxY; // Нижняя стенка верхнего зажима
  const lowerClampBottomY = lowerClampMaxY; // Нижняя стенка нижнего зажима
  
  // Масштабируем ТОЛЬКО shapes слоя CLAMP, ОТ НИЖНЕЙ СТЕНКИ ВВЕРХ
  return shapes.map(shape => {
    // Если это НЕ CLAMP - возвращаем без изменений
    if (shape.layer !== 'CLAMP') return shape;

    if (shape.type === 'line' || shape.type === 'polyline') {
      const newPoints = [...shape.points];
      // Масштабируем ТОЛЬКО Y координаты (нечетные индексы)
      for (let i = 1; i < newPoints.length; i += 2) {
        const y = newPoints[i];
        
        // Определяем к какому зажиму относится точка
        if (y <= upperClampMaxY) {
          // Верхний зажим - масштабируем от нижней стенки вверх
          // Нижняя стенка (upperClampMaxY) остаётся на месте
          newPoints[i] = upperClampBottomY - (upperClampBottomY - y) * scaleY;
        } else {
          // Нижний зажим - масштабируем от нижней стенки вверх
          // Нижняя стенка (lowerClampMaxY) остаётся на месте
          newPoints[i] = lowerClampBottomY - (lowerClampBottomY - y) * scaleY;
        }
      }
      // X координаты (четные индексы) НЕ трогаем!
      return { ...shape, points: newPoints };
    } else if (shape.type === 'circle') {
      const y = shape.y;
      let newY = y;
      let newRadius = shape.radius * scaleY;
      
      if (y <= upperClampMaxY) {
        // Верхний зажим - масштабируем от нижней стенки вверх
        newY = upperClampBottomY - (upperClampBottomY - y) * scaleY;
      } else {
        // Нижний зажим - масштабируем от нижней стенки вверх
        newY = lowerClampBottomY - (lowerClampBottomY - y) * scaleY;
      }
      
      // Масштабируем ТОЛЬКО Y и radius по Y, X НЕ трогаем
      return { ...shape, y: newY, radius: newRadius };
    } else if (shape.type === 'arc') {
      const y = shape.y;
      let newY = y;
      let newRadius = shape.radius * scaleY;
      
      if (y <= upperClampMaxY) {
        // Верхний зажим - масштабируем от нижней стенки вверх
        newY = upperClampBottomY - (upperClampBottomY - y) * scaleY;
      } else {
        // Нижний зажим - масштабируем от нижней стенки вверх
        newY = lowerClampBottomY - (lowerClampBottomY - y) * scaleY;
      }
      
      // Масштабируем ТОЛЬКО Y и radius по Y, X НЕ трогаем
      return { ...shape, y: newY, radius: newRadius };
    }

    return shape;
  });
}

/**
 * Вычисляет новые координаты точек привязки на основе реальных границ слоя CLAMP
 * после трансформации
 * 
 * @param {Array} shapes - Трансформированные shapes (уже нормализованные к (0,0))
 * @param {number} centerX - X координата центра (обычно ширина/2)
 * @returns {Array} - Массив точек привязки { absolute: true, x, y }
 */
export function calculateFastenerSnapPointsFromShapes(shapes, centerX = 20) {
  // Находим все CLAMP shapes и группируем их по Y-координатам
  const clampShapes = shapes.filter(s => s.layer === 'CLAMP');
  
  if (clampShapes.length === 0) {
    console.warn('CLAMP слой не найден в shapes');
    return [];
  }
  
  // Хомут — это связанные линии контура, а не разрыв в списке координат.
  // У YKRS зазор между хомутами начинается выше средней трети, поэтому
  // прежняя проверка схлопывала оба хомута в одну точку посередине планки.
  // Метка болта лежит внутри контура и отдельным хомутом не считается.
  const bands = [];
  const absorb = (y0, y1) => {
    let a = Math.min(y0, y1);
    let b = Math.max(y0, y1);
    let merged = true;
    while (merged) {
      merged = false;
      for (let i = 0; i < bands.length; i += 1) {
        const band = bands[i];
        if (b < band.y0 - 1 || a > band.y1 + 1) continue;
        a = Math.min(a, band.y0);
        b = Math.max(b, band.y1);
        bands.splice(i, 1);
        merged = true;
        break;
      }
    }
    bands.push({ y0: a, y1: b });
  };
  clampShapes.forEach((shape) => {
    if (shape.type === 'line' || shape.type === 'polyline') {
      const points = shape.points || [];
      if (points.length < 4) {
        if (points.length >= 2) absorb(points[1], points[1]);
        return;
      }
      for (let i = 0; i + 3 < points.length; i += 2) {
        absorb(points[i + 1], points[i + 3]);
      }
    } else if (shape.type === 'circle' || shape.type === 'arc') {
      absorb(shape.y - (shape.radius || 0), shape.y + (shape.radius || 0));
    }
  });

  const clamps = bands.filter((band) => band.y1 - band.y0 > 15);
  if (clamps.length === 0) {
    const yCoords = bands.flatMap((band) => [band.y0, band.y1]);
    if (yCoords.length === 0) return [];
    const centerY = (Math.min(...yCoords) + Math.max(...yCoords)) / 2;
    return [{ absolute: true, x: centerX, y: centerY }];
  }

  clamps.sort((a, b) => a.y0 - b.y0);
  return clamps.map((band) => ({
    absolute: true,
    x: centerX,
    y: (band.y0 + band.y1) / 2,
  }));
}

function clampBoxes(shapes) {
  const points = [];
  shapes.forEach((shape) => {
    if (shape.layer !== 'CLAMP') return;
    if (shape.type !== 'line' && shape.type !== 'polyline') return;
    const coords = shape.points || [];
    for (let i = 0; i < coords.length; i += 2) {
      points.push({ x: coords[i], y: coords[i + 1] });
    }
  });
  if (points.length === 0) return [];
  points.sort((a, b) => a.y - b.y);
  let gap = 0;
  let split = 0;
  for (let i = 0; i < points.length - 1; i += 1) {
    const distance = points[i + 1].y - points[i].y;
    if (distance > gap) {
      gap = distance;
      split = i;
    }
  }
  const groups = gap > 5 ? [points.slice(0, split + 1), points.slice(split + 1)] : [points];
  return groups.filter((group) => group.length > 0).map((group) => ({
    x0: Math.min(...group.map((point) => point.x)),
    x1: Math.max(...group.map((point) => point.x)),
    y0: Math.min(...group.map((point) => point.y)),
    y1: Math.max(...group.map((point) => point.y)),
  }));
}

/** Отверстие планки не рисуется, если его круг пересекает полосу хомута. */
export function hideFastenerHolesUnderClamps(shapes) {
  if (!shapes || shapes.length === 0) return shapes;
  const bands = [];
  shapes.forEach((shape) => {
    if (shape.layer !== 'CLAMP') return;
    if (shape.type !== 'line' && shape.type !== 'polyline') return;
    const coords = shape.points || [];
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < coords.length; i += 2) {
      minX = Math.min(minX, coords[i]);
      maxX = Math.max(maxX, coords[i]);
      minY = Math.min(minY, coords[i + 1]);
      maxY = Math.max(maxY, coords[i + 1]);
    }
    if (minX === Infinity) return;
    let joined = false;
    bands.forEach((band) => {
      const overlaps = maxX >= band.x0 - 0.6 && minX <= band.x1 + 0.6
        && maxY >= band.y0 - 0.6 && minY <= band.y1 + 0.6;
      if (!overlaps) return;
      band.x0 = Math.min(band.x0, minX);
      band.x1 = Math.max(band.x1, maxX);
      band.y0 = Math.min(band.y0, minY);
      band.y1 = Math.max(band.y1, maxY);
      joined = true;
    });
    if (!joined) bands.push({ x0: minX, x1: maxX, y0: minY, y1: maxY });
  });
  // Склеиваем полосы, которые сошлись через общую линию.
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < bands.length; i += 1) {
      for (let j = i + 1; j < bands.length; j += 1) {
        const a = bands[i];
        const b = bands[j];
        const overlaps = b.x1 >= a.x0 - 0.6 && b.x0 <= a.x1 + 0.6
          && b.y1 >= a.y0 - 0.6 && b.y0 <= a.y1 + 0.6;
        if (!overlaps) continue;
        a.x0 = Math.min(a.x0, b.x0);
        a.x1 = Math.max(a.x1, b.x1);
        a.y0 = Math.min(a.y0, b.y0);
        a.y1 = Math.max(a.y1, b.y1);
        bands.splice(j, 1);
        changed = true;
        break;
      }
      if (changed) break;
    }
  }
  if (bands.length === 0) return shapes;
  return shapes.filter((shape) => {
    if (shape.type !== 'circle' || shape.layer === 'CLAMP') return true;
    const radius = shape.radius || 0;
    return !bands.some((band) => (
      shape.x + radius > band.x0
      && shape.x - radius < band.x1
      && shape.y + radius > band.y0
      && shape.y - radius < band.y1
    ));
  });
}

/**
 * Верхняя кромка планки в DXF доходит только до левого края хомута: дальше её закрывает
 * выступающий хомут. Если хомут стал короче и кромку не перекрывает, дорисовываем её
 * до боковых линий планки. Участок под хомутом не рисуем.
 */
export function repairFastenerProfileContour(shapes) {
  if (!shapes || shapes.length === 0) return shapes;
  const segments = [];
  shapes.forEach((shape) => {
    if (shape.layer !== 'FASTENER') return;
    if (shape.type !== 'line' && shape.type !== 'polyline') return;
    const coords = shape.points || [];
    for (let i = 0; i < coords.length - 2; i += 2) {
      segments.push({
        x1: coords[i],
        y1: coords[i + 1],
        x2: coords[i + 2],
        y2: coords[i + 3],
      });
    }
  });
  const verticals = segments.filter((segment) => (
    Math.abs(segment.x1 - segment.x2) < 0.4 && Math.abs(segment.y1 - segment.y2) > 40
  ));
  if (verticals.length < 2) return shapes;
  const leftX = Math.min(...verticals.map((segment) => segment.x1));
  const rightX = Math.max(...verticals.map((segment) => segment.x1));
  if (rightX - leftX < 5) return shapes;
  const sides = verticals.filter((segment) => (
    Math.abs(segment.x1 - leftX) < 0.4 || Math.abs(segment.x1 - rightX) < 0.4
  ));
  const yEnds = [
    Math.min(...sides.flatMap((segment) => [segment.y1, segment.y2])),
    Math.max(...sides.flatMap((segment) => [segment.y1, segment.y2])),
  ];
  const clamps = clampBoxes(shapes);
  const extra = [];
  yEnds.forEach((y) => {
    // Кромку дорисовываем только если она в DXF уже начата и оборвана.
    // Целиком новой линии на торце планки нет: иначе под болтами появляется полоска.
    const hasEdge = segments.some((segment) => (
      Math.abs(segment.y1 - y) < 0.4
      && Math.abs(segment.y2 - y) < 0.4
      && Math.abs(segment.x1 - segment.x2) > 1
    ));
    if (!hasEdge) return;
    let gaps = [[leftX, rightX]];
    clamps.forEach((box) => {
      if (y < box.y0 - 0.4 || y > box.y1 + 0.4) return;
      gaps = gaps.flatMap(([from, to]) => {
        const parts = [];
        if (from < box.x0 - 0.2) parts.push([from, Math.min(to, box.x0)]);
        if (to > box.x1 + 0.2) parts.push([Math.max(from, box.x1), to]);
        return parts;
      });
    });
    gaps.forEach(([from, to]) => {
      if (to - from < 0.8) return;
      const covered = segments.some((segment) => (
        Math.abs(segment.y1 - y) < 0.4
        && Math.abs(segment.y2 - y) < 0.4
        && Math.min(segment.x1, segment.x2) <= from + 0.4
        && Math.max(segment.x1, segment.x2) >= to - 0.4
      ));
      if (covered) return;
      extra.push({
        type: 'line',
        layer: 'FASTENER',
        scalable: true,
        points: [from, y, to, y],
      });
    });
  });
  return extra.length === 0 ? shapes : shapes.concat(extra);
}

